import { motion } from "framer-motion";
import { QrCode, Swords, TrendingUp, Package, ArrowRight } from "lucide-react";
import { useLang, STR } from "@/i18n";

const STEP_META = [
  { n: "01", icon: QrCode, accent: "text-sky-300", tile: "border-sky-400/30 bg-sky-400/10" },
  { n: "02", icon: Swords, accent: "text-amber-300", tile: "border-amber-400/30 bg-amber-400/10" },
  { n: "03", icon: TrendingUp, accent: "text-emerald-300", tile: "border-emerald-400/30 bg-emerald-400/10" },
  { n: "04", icon: Package, accent: "text-orange-300", tile: "border-orange-400/30 bg-orange-400/10" },
];

export default function HowItWorks() {
  const { lang } = useLang();
  const t = STR[lang];

  return (
    <section id="como-funciona" data-testid="how-it-works" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute bottom-0 -right-40 w-[32rem] h-[32rem] rounded-full bg-sky-500/[0.07] blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="mono text-xs uppercase tracking-[0.3em] text-slate-500 mb-4"
          >
            {t.hiw_eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight"
          >
            {t.hiw_h2a}{" "}
            <span className="bg-gradient-to-r from-sky-300 to-amber-300 bg-clip-text text-transparent">
              {t.hiw_h2b}
            </span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-sky-400/25 bg-sky-400/5 px-5 py-4"
          >
            <QrCode className="w-5 h-5 text-sky-300 shrink-0" />
            <p className="text-sm text-slate-300 leading-relaxed">{t.hiw_sub}</p>
          </motion.div>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {STEP_META.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={`how-step-${s.n}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group relative rounded-3xl border border-white/10 bg-[#0c1122]/80 backdrop-blur p-6 sm:p-7 transition-all duration-300 hover:-translate-y-2 hover:border-white/25"
            >
              <div className="flex items-center justify-between mb-6">
                <span className={`mono font-bold text-4xl ${s.accent} opacity-90`}>{s.n}</span>
                <span className={`grid place-items-center w-11 h-11 rounded-2xl border ${s.tile} transition-transform duration-300 group-hover:scale-110`}>
                  <s.icon className={`w-5 h-5 ${s.accent}`} strokeWidth={2.2} />
                </span>
              </div>
              <h3 className="font-display font-bold text-base sm:text-lg text-white mb-2.5">
                {t[`hiw_s${i + 1}t`]}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">{t[`hiw_s${i + 1}d`]}</p>
              {i < 3 && (
                <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 w-5 h-5 text-slate-600 -translate-y-1/2 z-10" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
